export default function SVGVEditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - SVG Editor &amp; Vectorizer
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for SVG Editor &amp;
            Vectorizer.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I create and edit an SVG?
              </h3>
              <p className="mt-1">
                Tap <strong>New</strong> on the home screen for a blank
                canvas, or pick a template to start with. The editor has
                three tabs: Code (write raw SVG XML), Preview (live render),
                and Layers (tap any shape to change its fill colour,
                toggle visibility, or reorder).
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I convert a photo or logo to SVG?
              </h3>
              <p className="mt-1">
                Tap <strong>Vectorize</strong> on the home screen and pick
                a PNG or JPG. Adjust the colour count slider (fewer colours
                = simpler SVG) and the simplify slider (higher = fewer
                anchor points). Tap Convert — the conversion runs offline
                on your device. You can open the result in the editor or
                save it directly.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What kinds of images vectorize well?
              </h3>
              <p className="mt-1">
                Logos, icons, signatures, black-and-white line art, and flat
                illustrations give the cleanest results. Photos and images
                with gradients will look posterized — that&apos;s normal
                and expected. Lower the colour count for bolder, cleaner
                output; raise it for more detail.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does everything work offline?
              </h3>
              <p className="mt-1">
                Yes. The vectorizer, editor, and live preview all run
                entirely on your device. Network access is used only for
                showing ads and for the rotating cross-promotion banner.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I save or export?
              </h3>
              <p className="mt-1">
                From the editor overflow menu (three dots): <strong>Save
                as .svg</strong> saves inside the app, <strong>Save to
                device</strong> opens the system save dialog so you can
                put the file anywhere — Downloads, Drive, a cloud sync
                folder, or external storage. <strong>Export PNG</strong>{" "}
                and <strong>Export JPG</strong> rasterize the SVG at the
                canvas resolution.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Can I use the output with Cricut or Silhouette?
              </h3>
              <p className="mt-1">
                Yes. SVG is the standard format those cutting machines
                import directly. The Cricut export preset (available with
                the Pro upgrade) strips fills and ensures single-layer cut
                paths, matching the format those tools expect.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How does the free vectorizer limit work?
              </h3>
              <p className="mt-1">
                The free tier allows 3 conversions per day. The counter
                resets every calendar day on your device. The Pro upgrade
                removes the limit entirely.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. You can remove all
                ads with a one-time Pro upgrade from Settings, which also
                unlocks unlimited image conversions and extra features.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing. What do I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the app. Make sure you have the
                latest version from Google Play. If a specific SVG or image
                crashes the app, email us with the file attached.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details
            in your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/svgvedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/svgvedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/svgvedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/svgvedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> SVG Editor &amp; Vectorizer
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.svgvedit
            </li>
            <li>
              <strong>Platform:</strong> Android
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
